/**
 * Re-homing of ABAX CDN URLs onto the origin the document is actually served
 * from, and the canonical-identity helper that survives it.
 *
 * Federated bundles are registered with absolute CDN URLs by their deploy
 * pipelines, and every federated script is injected with
 * `crossorigin="anonymous"` (required for SRI). The CDN serves no
 * `Access-Control-Allow-Origin`, so those loads fail closed on any document
 * whose origin is not the CDN's. That was invisible while the platform was
 * served from one host, because the CDN path is same-origin there by
 * construction; it is not invisible once the same app answers on a second
 * hostname that proxies the CDN path through its own origin.
 *
 * Re-homing turns the cross-origin load back into a same-origin one, which is
 * the shape every standalone ABAX host already implements for itself with an
 * nginx bridge. It carries the whole bundle, not just the entry: MFEs set
 * `__webpack_public_path__` from `document.currentScript.src`, so every lazy
 * chunk follows the origin the entry was loaded from.
 *
 * This module is the only place in the platform that knows CDN locations.
 */

export interface CdnOrigin {
  /** Scheme + host the bundles are published under. */
  origin: string;
  /** Absolute, slash-terminated path they are served beneath, with at least one segment. */
  pathPrefix: string;
}

/**
 * The CDN locations that ship with the platform.
 *
 * These are also the ONLY locations that confer identity (see
 * `canonicalCdnIdentity`). Runtime settings can add transport locations for a
 * new environment, but they cannot mint an identity, because the drift alarm
 * that identity feeds must not be retunable by the same untrusted config a typo
 * or an attacker could reach.
 */
export const DEFAULT_CDN_ORIGINS: readonly CdnOrigin[] = Object.freeze([
  Object.freeze({ origin: 'https://staging.track2find.com', pathPrefix: '/abaxcdnstaging/' }),
  Object.freeze({ origin: 'https://abax.cloud', pathPrefix: '/abaxcdn/' }),
]);

/**
 * Cross-bundle home for the effective list.
 *
 * `@abax/platform` is NOT a Module Federation singleton — the shell's `shared`
 * block covers vue and single-spa only — so every MFE carries its own copy of
 * this module with its own module state. A list configured by the host would
 * otherwise reach the host's copy and no other, leaving a `ParcelLoader` inside
 * an MFE re-homing against different locations than the shell. A global keyed by
 * `Symbol.for` is shared across every copy in the realm, which is the mechanism
 * the AFF scope key already uses for the same reason.
 */
const REGISTRY = Symbol.for('abax.platform.cdnOrigins');

interface Registry {
  origins: readonly CdnOrigin[];
}

function registry(): Registry {
  const host = globalThis as unknown as Record<symbol, Registry | undefined>;
  let current = host[REGISTRY];
  if (!current) {
    current = { origins: DEFAULT_CDN_ORIGINS };
    host[REGISTRY] = current;
  }
  return current;
}

/**
 * A prefix must be one of the COMPILED namespaces.
 *
 * Settings choose which HOST serves a namespace; they do not get to invent a
 * namespace. `isCdnPath` cannot check an origin — by the time it runs, the URL
 * has been re-homed onto the document and the CDN origin is gone — so a prefix
 * is the whole of what it matches on, and it is what `validateRemoteUrl` accepts
 * as a same-origin script path. A configurable prefix would therefore let one
 * line of runtime config mark an arbitrary path on the document's own origin as
 * loadable script, on exactly the hosts that sit outside the canonical zones and
 * have no other rule covering them. The nginx bridge hardcodes the same two
 * namespaces, so a novel prefix could not have been served anyway.
 */
function isUsablePrefix(prefix: string): boolean {
  return DEFAULT_CDN_ORIGINS.some((cdn) => cdn.pathPrefix === prefix);
}

function isUsable(entry: unknown): entry is CdnOrigin {
  if (typeof entry !== 'object' || entry === null) return false;
  const { origin, pathPrefix } = entry as Partial<CdnOrigin>;
  if (typeof origin !== 'string' || typeof pathPrefix !== 'string') return false;
  if (!isUsablePrefix(pathPrefix)) return false;
  try {
    const parsed = new URL(origin);
    return (
      parsed.origin === origin && (parsed.protocol === 'https:' || parsed.protocol === 'http:')
    );
  } catch {
    return false;
  }
}

/**
 * Add this environment's CDN locations to the built-in ones. Call once during
 * config load, before anything resolves a remote.
 *
 * ADDITIVE and idempotent: the defaults are always recomputed as the base, so a
 * partial or typoed list can never remove a real ABAX CDN and silently switch
 * re-homing off for it, and a later call with no list restores exactly the
 * defaults rather than leaving the previous call's state behind. An extra entry
 * can only bind another HOST to a namespace that is already compiled in, so it
 * adds a place a bundle may come FROM and never a path anything will accept.
 * Malformed entries are dropped rather than thrown on: a config typo should cost
 * that entry, not the boot.
 *
 * The registry is page-level state the HOST owns. It is not an access boundary:
 * every bundle in the realm can reach the same `Symbol.for` slot directly, so
 * the export list decides ergonomics, not authority. Last writer wins, which is
 * why this belongs in config load and nowhere else.
 */
export function configureCdnOrigins(list: unknown): readonly CdnOrigin[] {
  const extra = Array.isArray(list) ? list.filter(isUsable) : [];
  const merged = [...DEFAULT_CDN_ORIGINS];
  for (const entry of extra) {
    if (!merged.some((m) => m.origin === entry.origin && m.pathPrefix === entry.pathPrefix)) {
      merged.push(Object.freeze({ origin: entry.origin, pathPrefix: entry.pathPrefix }));
    }
  }
  // Frozen because the stored list IS the returned one, and `readonly` is a
  // compile-time claim only: a cast plus a push would otherwise reach the
  // registry without passing `isUsable` at all.
  registry().origins = Object.freeze(merged);
  return registry().origins;
}

/** The CDN locations currently in effect, shared across every copy of this package. */
export function getCdnOrigins(): readonly CdnOrigin[] {
  return registry().origins;
}

function documentOrigin(): string | null {
  return typeof window === 'undefined' ? null : window.location.origin;
}

/**
 * True when the document is served from a canonical CDN origin rather than a
 * host proxying one.
 *
 * Reads the COMPILED defaults, never the configured list. This predicate SKIPS
 * re-homing, so taking it from settings hands an untrusted list an off switch:
 * one entry naming the branded host itself would make every CDN URL identity
 * and restore in full the CORS outage this whole change exists to end. Owning a
 * CDN namespace is a compiled fact; settings can add a transport location, not
 * claim to be the canonical home of one.
 */
function isCanonicalCdnDocument(origin: string): boolean {
  return DEFAULT_CDN_ORIGINS.some((cdn) => cdn.origin === origin);
}

/**
 * An encoded path separator or dot segment survives `new URL()` normalisation,
 * so a prefix test alone can be walked out of by a downstream proxy that decodes
 * before it routes. Anything carrying one is refused rather than interpreted.
 *
 * `%25` is refused too, because it is the escape that reaches the others: a
 * proxy that decodes twice turns `%252e` into `.` while a single-encoding test
 * sees nothing wrong with it. Nothing legitimate here carries a percent — these
 * are webpack content-hashed filenames.
 */
export function hasEncodedSeparator(pathname: string): boolean {
  return /%2e|%2f|%5c|%25/i.test(pathname);
}

/**
 * True when a same-origin path sits under one of the CDN namespaces.
 *
 * Reads the COMPILED defaults. This is what `validateRemoteUrl` accepts as a
 * same-origin script path, and an origin cannot take part in the decision: by
 * the time a URL reaches here it has been re-homed onto the document and the CDN
 * origin it came from is gone. The prefix is therefore the whole of the test, so
 * it stays a compiled fact. `isUsable` refuses a configured entry on any other
 * prefix, which makes this identical to reading the effective list today; it is
 * stated here as well so the property survives a later change over there.
 */
export function isCdnPath(pathname: string): boolean {
  if (hasEncodedSeparator(pathname)) return false;
  return DEFAULT_CDN_ORIGINS.some((cdn) => pathname.startsWith(cdn.pathPrefix));
}

/**
 * Rewrite an absolute ABAX CDN URL onto the current document's origin, so the
 * asset is fetched same-origin and needs no CORS header.
 *
 * Identity in these cases, each deliberate:
 *
 * - The URL is already same-origin, relative, or not a CDN URL at all. Nothing
 *   to do, and a relative URL is left for the caller's own base resolution.
 * - **The document is itself on a canonical CDN origin.** Re-homing there could
 *   only move a URL between CDN namespaces — a staging URL onto the production
 *   host, say — and the receiving host serves no such path. The canonical hosts
 *   are already same-origin with the namespace they own, so there is nothing to
 *   gain and a broken path to lose.
 * - The path carries an encoded separator.
 * - No window (SSR, a unit test without a DOM).
 *
 * The pathname, query and fragment are preserved exactly; only the origin moves. The
 * output origin is ALWAYS the document's own, which is what bounds the damage a
 * bad location list can do: a wrong entry misroutes a load into a 404 on this
 * origin and can never send it somewhere else.
 */
export function rehomeCdnUrlToDocumentOrigin(url: string): string {
  if (!url) return url;
  const origin = documentOrigin();
  if (!origin || isCanonicalCdnDocument(origin)) return url;
  try {
    const parsed = new URL(url);
    if (parsed.origin === origin) return url;
    if (hasEncodedSeparator(parsed.pathname)) return url;
    const cdn = getCdnOrigins().find(
      (c) => parsed.origin === c.origin && parsed.pathname.startsWith(c.pathPrefix),
    );
    if (!cdn) return url;
    return `${origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

/**
 * The CDN origin and namespace that owns a URL, whether it arrives canonical or
 * already re-homed, or `null` when it is not a CDN URL.
 *
 * Reads the COMPILED defaults, never the configured list. Drift is a correctness
 * alarm about the shell loading one environment's bundle while the backend
 * advertises another's, and an alarm untrusted settings can retune is not an
 * alarm. A configured location therefore participates in re-homing but confers
 * no identity, so a crafted entry cannot make production and staging read as the
 * same source.
 */
export function canonicalCdnIdentity(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (hasEncodedSeparator(parsed.pathname)) return null;
    const direct = DEFAULT_CDN_ORIGINS.find(
      (cdn) => parsed.origin === cdn.origin && parsed.pathname.startsWith(cdn.pathPrefix),
    );
    if (direct) return `${direct.origin}${direct.pathPrefix}`;
    const origin = documentOrigin();
    if (origin && parsed.origin === origin) {
      const rehomed = DEFAULT_CDN_ORIGINS.find((cdn) => parsed.pathname.startsWith(cdn.pathPrefix));
      if (rehomed) return `${rehomed.origin}${rehomed.pathPrefix}`;
    }
    return null;
  } catch {
    return null;
  }
}
